import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from './AuthContext';

// ── Colour utilities ────────────────────────────────────────────────────────

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

function getTextOnColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#18191b' : '#ffffff';
}

export function applyAccentColor(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return;
  const [h, s, l] = hexToHsl(hex);

  const lightVars = {
    '--color-brand':        hex,
    '--color-brand-light':  hslToHex(h, clamp(s - 5, 0, 100), clamp(l + 12, 0, 95)),
    '--color-brand-dark':   hslToHex(h, clamp(s + 5, 0, 100), clamp(l - 12, 5, 100)),
    '--color-brand-hover':  hslToHex(h, s, clamp(l - 6, 5, 100)),
    '--color-brand-pale':   hslToHex(h, clamp(s * 0.3, 3, 30), 93),
    '--color-brand-subtle': hslToHex(h, clamp(s * 0.2, 2, 20), 96),
    '--color-accent':       hslToHex(h, clamp(s - 8, 0, 100), clamp(l + 18, 0, 95)),
    '--text-on-brand':      getTextOnColor(hex),
    '--focus-ring':         `0 0 0 3px ${hex}40`,
  };

  const darkVars = {
    '--color-brand':        hslToHex(h, clamp(s + 5, 0, 100), clamp(l + 10, 20, 78)),
    '--color-brand-light':  hslToHex(h, clamp(s + 10, 0, 100), clamp(l + 18, 25, 82)),
    '--color-brand-hover':  hslToHex(h, s, clamp(l + 5, 20, 75)),
    '--color-brand-pale':   hslToHex(h, clamp(s * 0.4, 5, 35), 13),
    '--color-brand-subtle': hslToHex(h, clamp(s * 0.3, 3, 25), 16),
    '--color-accent':       hslToHex(h, clamp(s - 5, 0, 100), clamp(l + 22, 25, 82)),
    '--text-on-brand':      getTextOnColor(hslToHex(h, clamp(s + 5, 0, 100), clamp(l + 10, 20, 78))),
    '--focus-ring':         `0 0 0 3px ${hex}4d`,
  };

  // Use a <style> element (NOT inline styles) so [data-theme='dark'] rules can
  // override the :root defaults through normal CSS cascade specificity.
  // Inline styles would permanently win over any attribute-selector rule.
  let styleEl = document.getElementById('floora-accent');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'floora-accent';
    document.head.appendChild(styleEl);
  }
  const lightCss = Object.entries(lightVars).map(([k, v]) => `${k}:${v}`).join(';');
  const darkCss  = Object.entries(darkVars).map(([k, v]) => `${k}:${v}`).join(';');
  styleEl.textContent = `:root{${lightCss}}[data-theme='dark']{${darkCss}}`;
}

function clearAccentColor() {
  // Clean up both the new unified element and the old split element (if present).
  document.getElementById('floora-accent')?.remove();
  document.getElementById('floora-accent-dark')?.remove();
  // Also wipe any legacy inline custom-property overrides.
  const props = [
    '--color-brand', '--color-brand-light', '--color-brand-dark',
    '--color-brand-hover', '--color-brand-pale', '--color-brand-subtle',
    '--color-accent', '--text-on-brand', '--focus-ring',
  ];
  props.forEach((k) => document.documentElement.style.removeProperty(k));
}

// ── Theme context ────────────────────────────────────────────────────────────

const ThemeContext = createContext(null);

const LS_KEY = 'floora-theme-pref';
const VALID = ['light', 'dark', 'system'];

function readLocalPref() {
  const v = localStorage.getItem(LS_KEY);
  return VALID.includes(v) ? v : 'system';
}

export function ThemeProvider({ children }) {
  const { user, profile, companyId } = useAuth();

  // User's raw selection — 'light' | 'dark' | 'system'
  // Seeded from localStorage so the DOM is correct before async data arrives.
  const [themePref, setThemePrefState] = useState(readLocalPref);

  // OS dark-mode state — kept as separate reactive value so 'system' responds live.
  const [sysDark, setSysDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  // Resolved value — what actually gets applied to the DOM.
  const theme = themePref === 'system' ? (sysDark ? 'dark' : 'light') : themePref;

  // Guard: once the user makes an explicit in-session choice, company-default
  // changes from Firestore won't override it.
  const userOverride = useRef(false);

  // ── OS preference listener (always active) ────────────────────────────────
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSysDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ── Apply resolved theme to DOM ──────────────────────────────────────────
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // ── Sync preference from Firestore profile (once per login) ─────────────
  // Keys on BOTH user?.uid and profile so it fires when either changes:
  // - user?.uid changes: login / logout (Firebase Auth object has .uid)
  // - profile changes: Firestore doc loaded after auth resolves
  // NOTE: we use user?.uid instead of profile?.uid because Firestore's
  // .data() does NOT include the document ID, making profile?.uid always
  // undefined — so the dep would never change and the effect would never run.
  useEffect(() => {
    if (!user) {
      userOverride.current = false; // Reset override on logout
      return;
    }
    if (!profile) return; // Auth resolved but profile not yet fetched
    const pref = profile.themePreference;
    if (pref && VALID.includes(pref)) {
      setThemePrefState(pref);
      localStorage.setItem(LS_KEY, pref);
    }
  }, [user?.uid, profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Company settings listener (accent colour + company theme default) ─────
  const [companyDefault, setCompanyDefault] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(doc(db, COLLECTIONS.SETTINGS, companyId), (snap) => {
      const data = snap.data();
      const color = data?.accentColor;
      if (color) applyAccentColor(color);
      else clearAccentColor();
      if (data?.themeDefault) setCompanyDefault(data.themeDefault);
    });
  }, [companyId]);

  // Apply company default only as a fallback:
  // skip if the user has made an explicit in-session choice, or if they
  // have a personal preference stored in their Firestore profile.
  useEffect(() => {
    if (!companyDefault || !VALID.includes(companyDefault)) return;
    if (userOverride.current) return;
    if (profile?.themePreference) return;
    setThemePrefState(companyDefault);
    localStorage.setItem(LS_KEY, companyDefault);
  }, [companyDefault]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Public API ────────────────────────────────────────────────────────────
  const setTheme = async (pref) => {
    if (!VALID.includes(pref)) return;
    userOverride.current = true;
    setThemePrefState(pref);
    localStorage.setItem(LS_KEY, pref);
    if (user) {
      try {
        await updateDoc(doc(db, COLLECTIONS.USERS, user.uid), {
          themePreference: pref,
          updatedAt: new Date(),
        });
      } catch {
        // Non-critical — theme is already applied locally
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, themePref, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
